/**
 * Rewardful affiliate tracking.
 *
 * The script is served same-origin from `/static/rw.js` (a CloudFront proxy to r.wdfl.co,
 * `infra-dev/app/cloudfront_rewardful.tf`) because ad blockers filter r.wdfl.co by name.
 * Rewardful owns the referral cookie; we only ever read `window.Rewardful`.
 *
 * Two funnels, and a campaign belongs to exactly one:
 *
 *   cookie      the visitor arrived via an affiliate link and the campaign is one the
 *               server maps to a coupon, so the discount applies with no user input
 *   promo code  every other campaign: Rewardful issues its affiliates unique Stripe
 *               promotion codes and credits them from the redemption itself, so a typed
 *               code needs nothing from us — Stripe's own box collects it
 *
 * Every read tolerates the script being absent, because for ad-blocked visitors it always
 * is. That is the same code path as a stage with affiliate tracking switched off, which is
 * why no non-prod special case is needed.
 */

import { REWARDFUL } from '@/config/constants'

interface RewardfulCampaign {
  id?: string
}

interface RewardfulGlobal {
  referral?: string | null
  campaign?: RewardfulCampaign | null
  coupon?: { id?: string } | null
}

declare global {
  interface Window {
    Rewardful?: RewardfulGlobal
    rewardful?: ((...args: unknown[]) => void) & { q?: unknown[] }
    _rwq?: string
  }
}

function global(): RewardfulGlobal | undefined {
  return typeof window === 'undefined' ? undefined : window.Rewardful
}

/** True once the tracking script has loaded and populated its globals. */
export function isReady(): boolean {
  return global() != null
}

/** The current visitor's referral id, or null when not referred (or script absent). */
export function getReferral(): string | null {
  return global()?.referral || null
}

/** The campaign the referral belongs to. Gates which coupons the server will honour. */
export function getCampaign(): string | null {
  return global()?.campaign?.id || null
}

/**
 * The coupon Rewardful assigned to THIS visitor.
 *
 * It has to come from here rather than being derived server-side from the campaign: a
 * campaign has several coupons, one per affiliate (prod carries three sharing the
 * 15%-once shape). The server refuses any coupon its campaign is not configured to allow,
 * so sending it is safe.
 */
export function getCoupon(): string | null {
  return global()?.coupon?.id || null
}

/**
 * Load the tracking script, once, if this stage has affiliate tracking enabled.
 *
 * The stub queue (`window.rewardful`) must exist BEFORE the script does, so calls made
 * while it loads are replayed rather than lost — that is what Rewardful's inline snippet
 * does. Injecting from here instead of index.html keeps it switchable per stage through
 * runtime config, with no rebuild.
 */
export function loadRewardful(): void {
  if (!REWARDFUL.enabled || typeof window === 'undefined') return
  if (window._rwq) return // already loaded (or loading)

  window._rwq = 'rewardful'
  window.rewardful =
    window.rewardful ||
    function (...args: unknown[]) {
      const fn = window.rewardful as { q?: unknown[] }
      fn.q = fn.q || []
      fn.q.push(args)
    }

  const script = document.createElement('script')
  script.async = true
  // Relative on purpose: survives next.edgetotrade.com -> app.edgetotrade.com.
  script.src = REWARDFUL.scriptPath
  script.setAttribute('data-rewardful', REWARDFUL.apiKey)
  document.head.appendChild(script)
}

/**
 * The affiliate fields to send with a checkout request. Always safe to spread: an
 * unreferred visitor, or one with the script blocked, contributes nothing.
 *
 * Attribution (`referral`) and discount (`affiliateCoupon`) are independent. The referral
 * alone decides who earns the commission; two affiliates in one campaign share a discount
 * and are different people.
 */
export function checkoutAttribution(): {
  referral?: string
  campaign?: string
  affiliateCoupon?: string
} {
  const referral = getReferral()
  const campaign = getCampaign()
  const coupon = getCoupon()
  return {
    ...(referral ? { referral } : {}),
    ...(campaign ? { campaign } : {}),
    ...(coupon ? { affiliateCoupon: coupon } : {}),
  }
}
